import React, { useState, useRef, useCallback, useEffect } from 'react';
import type { RefObject } from 'react';
import { Clock, Key } from 'lucide-react';
import type { ChartElement } from '@shared/types';

interface ElementRendererProps {
  element: ChartElement;
  selected: boolean;
  inMultiSelect?: boolean;
  dimmed?: boolean;
  readOnly?: boolean;
  autoEdit?: boolean;
  onAutoEditDone?: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onResize: (handle: string, e: React.MouseEvent | React.TouchEvent) => void;
  onUpdateContent: (content: Record<string, any>) => void;
  onEditingChanged?: (editing: boolean) => void;
}

export function ElementRenderer({ element, selected, inMultiSelect, dimmed, readOnly, autoEdit, onAutoEditDone, onMouseDown, onTouchStart, onResize, onUpdateContent, onEditingChanged }: ElementRendererProps) {
  // editingLatched: user-initiated editing (double-click) or latched after autoEdit fires
  const [editingLatched, setEditingLatched] = useState(false);
  // Derive editing synchronously so the input mounts in the same render as autoEdit=true.
  // This keeps mobile keyboard context alive (no async useEffect delay).
  const editing = editingLatched || (!readOnly && !!autoEdit);

  const contentRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevEditingRef = useRef(false);
  const onEditingChangedRef = useRef(onEditingChanged);
  onEditingChangedRef.current = onEditingChanged;

  useEffect(() => {
    if (!selected) setEditingLatched(false);
  }, [selected]);

  useEffect(() => {
    if (autoEdit && !readOnly) {
      // Latch editing so it stays true after autoEdit prop is cleared by parent
      setEditingLatched(true);
      onAutoEditDone?.();
    }
  }, [autoEdit]);

  // Notify parent when editing state changes (drives songForm width management).
  useEffect(() => {
    const wasEditing = prevEditingRef.current;
    prevEditingRef.current = editing;
    if (wasEditing !== editing) {
      onEditingChangedRef.current?.(editing);
    }
  }, [editing]);

  const isAutoResizeType = ['lyrics', 'notes', 'freetext', 'chord', 'songHeader', 'songForm'].includes(element.type);

  useEffect(() => {
    if (editing && textareaRef.current) {
      const ta = textareaRef.current;
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
      ta.select();
    }
  }, [editing]);
  const style: React.CSSProperties = {
    position: 'absolute',
    left: element.x,
    top: element.y,
    width: element.width,
    height: (editing && isAutoResizeType) ? 'auto' : element.height,
    minHeight: (editing && isAutoResizeType) ? element.height : undefined,
    zIndex: element.zIndex + 1,
    cursor: selected ? 'move' : 'pointer',
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly) return;
    setEditingLatched(true);
  };

  const renderContent = () => {
    const fontSize = element.content.fontSize ? `${element.content.fontSize}px` : undefined;
    const textAlign = (element.content.alignment || 'left') as React.CSSProperties['textAlign'];
    const fontWeight = element.content.fontWeight || 'normal';
    const fontStyle = element.content.fontStyle || 'normal';

    switch (element.type) {
      case 'title':
        return editing ? (
          <input
            autoFocus
            value={element.content.text}
            onChange={e => onUpdateContent({ text: e.target.value })}
            onFocus={e => e.target.select()}
            onBlur={() => setEditingLatched(false)}
            onKeyDown={e => e.key === 'Enter' && setEditingLatched(false)}
            className="w-full font-bold text-gray-900 bg-transparent border-none focus:outline-none px-2 pt-[2px] pb-[2px]"
            style={{ fontSize: fontSize || '20px', textAlign, lineHeight: 1.15 }}
          />
        ) : (
          <div
            className="w-full h-full flex items-start px-2 pt-[2px] pb-[2px] font-bold text-gray-900 truncate"
            style={{ fontSize: fontSize || '20px', textAlign, justifyContent: alignToFlex(textAlign), lineHeight: 1.15 }}
          >
            {element.content.text || 'Title'}
          </div>
        );

      case 'tempo':
        return editing ? (
          <input
            autoFocus
            type="number"
            value={element.content.bpm}
            onChange={e => onUpdateContent({ bpm: Number(e.target.value) })}
            onFocus={e => e.target.select()}
            onBlur={() => setEditingLatched(false)}
            onKeyDown={e => e.key === 'Enter' && setEditingLatched(false)}
            className="w-full h-full text-sm text-gray-800 bg-transparent border-none focus:outline-none px-2"
          />
        ) : (
          <div className="w-full h-full flex items-center gap-1 px-2 text-sm text-gray-800">
            <Clock className="w-3 h-3 text-gray-500 shrink-0" />
            <span className="font-semibold">{element.content.bpm || '?'}</span> BPM
          </div>
        );

      case 'key':
        return editing ? (
          <input
            autoFocus
            value={element.content.key}
            onChange={e => onUpdateContent({ key: e.target.value })}
            onFocus={e => e.target.select()}
            onBlur={() => setEditingLatched(false)}
            onKeyDown={e => e.key === 'Enter' && setEditingLatched(false)}
            className="w-full h-full text-sm text-gray-800 bg-transparent border-none focus:outline-none px-2"
          />
        ) : (
          <div className="w-full h-full flex items-center gap-1 px-2 text-sm text-gray-800">
            <Key className="w-3 h-3 text-gray-500 shrink-0" />
            Key: <span className="font-semibold">{element.content.key || '?'}</span>
          </div>
        );

      case 'section':
        return renderSection(element, editing, setEditingLatched, onUpdateContent);

      case 'chord':
        return renderChord(element, editing, setEditingLatched, onUpdateContent, textareaRef);

      case 'lyrics':
        return renderTextBlock(element, editing, setEditingLatched, onUpdateContent, textareaRef, {
          placeholder: 'Lyrics...',
          defaultStyle: 'italic',
          className: '',
        });

      case 'notes':
        return renderTextBlock(element, editing, setEditingLatched, onUpdateContent, textareaRef, {
          placeholder: 'Notes...',
          defaultStyle: 'normal',
          className: 'bg-yellow-50 border border-yellow-200 rounded',
        });

      case 'freetext':
        return renderTextBlock(element, editing, setEditingLatched, onUpdateContent, textareaRef, {
          placeholder: 'Text...',
          defaultStyle: 'normal',
          className: '',
        });

      case 'image':
        return element.content.url ? (
          <img src={element.content.url} alt="" className="w-full h-full object-contain" draggable={false} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs bg-gray-100 border border-dashed border-gray-300 rounded">
            No Image
          </div>
        );

      case 'divider': {
        const isVertical = element.content.orientation === 'vertical';
        return isVertical ? (
          <div className="w-full h-full flex justify-center items-center relative">
            <div className="h-full border-l-2 border-gray-300" />
            {element.content.label && (
              <span
                className="absolute top-1/2 -translate-y-1/2 bg-white py-1 text-xs text-gray-500"
                style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
              >
                {element.content.label}
              </span>
            )}
          </div>
        ) : (
          <div className="w-full h-full flex items-center relative">
            <div className="w-full border-t-2 border-gray-300" />
            {element.content.label && (
              <span className="absolute left-1/2 -translate-x-1/2 bg-white px-2 text-xs text-gray-500">
                {element.content.label}
              </span>
            )}
          </div>
        );
      }

      case 'link':
        return (
          <div
            className={`w-full h-full flex items-start gap-2 px-3 pt-[2px] pb-[2px] bg-blue-50 border border-blue-200 rounded-lg transition-colors ${readOnly && element.content.url ? 'cursor-pointer hover:bg-blue-100' : ''}`}
            onClick={(e) => {
              if (readOnly && element.content.url) {
                e.stopPropagation();
                e.preventDefault();
                window.open(element.content.url, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            <span className="text-blue-500 text-base shrink-0" style={{ lineHeight: 1.2 }}>🔗</span>
            <span className="text-sm font-medium text-blue-700 truncate" style={{ lineHeight: 1.2 }}>
              {element.content.label || 'Link'}
            </span>
          </div>
        );

      case 'repeat':
        return editing ? (
          <input
            autoFocus
            type="number"
            min={1}
            value={element.content.count}
            onChange={e => onUpdateContent({ count: Math.max(1, Number(e.target.value)) })}
            onFocus={e => e.target.select()}
            onBlur={() => setEditingLatched(false)}
            onKeyDown={e => e.key === 'Enter' && setEditingLatched(false)}
            className="w-full text-left font-bold text-gray-900 bg-transparent border-none focus:outline-none px-2 pt-[2px] pb-[2px]"
            style={{ fontSize: '18px', lineHeight: 1.25 }}
          />
        ) : (
          <div className="w-full h-full px-2 pt-[2px] pb-[2px] font-bold text-gray-900 whitespace-pre-wrap overflow-hidden" style={{ fontSize: '18px', lineHeight: 1.25 }}>
            x{element.content.count || 2}
          </div>
        );

      case 'songHeader': {
        const { title = '', artist = '', key = '', bpm } = element.content;
        const subtitleParts: string[] = [];
        if (artist) subtitleParts.push(artist);
        if (key) subtitleParts.push(`Key: ${key}`);
        if (bpm) subtitleParts.push(`${bpm} BPM`);
        const subtitle = subtitleParts.join('  |  ');
        return (
          <div className="w-full h-full flex flex-col justify-start px-2 pt-[2px] pb-[2px] select-none">
            <div className="font-bold text-gray-900 truncate" style={{ fontSize: '24px', lineHeight: 1.2 }}>
              {title || <span className="text-gray-400">Song Title</span>}
            </div>
            {subtitle && (
              <div className="text-gray-500 truncate" style={{ fontSize: '16px', lineHeight: 1.2 }}>
                {subtitle}
              </div>
            )}
          </div>
        );
      }

      case 'songForm':
        return renderSongForm(element, editing, onUpdateContent);

      default:
        return <div className="w-full h-full bg-gray-100" />;
    }
  };

  let handles = selected ? ['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'] : [];
  if (element.type === 'divider' && selected) {
    handles = element.content.orientation === 'vertical' ? ['n', 's'] : ['e', 'w'];
  }

  return (
    <div
      data-chart-element
      style={style}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onDoubleClick={handleDoubleClick}
      className={`group touch-manipulation transition-opacity ${
        selected
          ? (inMultiSelect
              ? 'outline outline-1 outline-blue-400/50 outline-offset-1'
              : 'outline outline-2 outline-blue-500 outline-offset-1')
          : 'hover:outline hover:outline-1 hover:outline-blue-300 hover:outline-offset-1'
      } ${dimmed ? 'opacity-40' : ''}`}
    >
      {renderContent()}

      {handles.map(handle => (
        <ResizeHandle
          key={handle}
          handle={handle}
          onResize={e => onResize(handle, e)}
        />
      ))}
    </div>
  );
}

function ResizeHandle({ handle, onResize }: { handle: string; onResize: (e: React.MouseEvent | React.TouchEvent) => void }) {
  const cursors: Record<string, string> = {
    nw: 'nw-resize', ne: 'ne-resize', sw: 'sw-resize', se: 'se-resize',
    n: 'n-resize', s: 's-resize', e: 'e-resize', w: 'w-resize',
  };

  const positions: Record<string, React.CSSProperties> = {
    nw: { top: -5, left: -5 },
    ne: { top: -5, right: -5 },
    sw: { bottom: -5, left: -5 },
    se: { bottom: -5, right: -5 },
    n: { top: -4, left: '50%', transform: 'translateX(-50%)' },
    s: { bottom: -4, left: '50%', transform: 'translateX(-50%)' },
    e: { top: '50%', right: -4, transform: 'translateY(-50%)' },
    w: { top: '50%', left: -4, transform: 'translateY(-50%)' },
  };

  const isCorner = ['nw', 'ne', 'sw', 'se'].includes(handle);

  return (
    <div
      onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onResize(e); }}
      onTouchStart={e => { e.stopPropagation(); onResize(e); }}
      className={`absolute ${isCorner ? 'w-3 h-3' : 'w-2.5 h-2.5'} bg-blue-500 border border-white rounded-sm`}
      style={{ ...positions[handle], cursor: cursors[handle], zIndex: 999 }}
    />
  );
}

function renderSection(
  element: ChartElement,
  editing: boolean,
  setEditing: (v: boolean) => void,
  onUpdateContent: (c: Record<string, any>) => void
) {
  const sectionStyle = element.content.style || 'header-bar';
  const fontSize = element.content.fontSize ? `${element.content.fontSize}px` : '13px';
  const textAlign = (element.content.alignment || 'left') as React.CSSProperties['textAlign'];
  const label = element.content.label || element.content.type || 'Section';

  const sectionTextStyle: React.CSSProperties = { fontSize, textAlign, justifyContent: alignToFlex(textAlign), lineHeight: 1.2 };

  if (editing) {
    return (
      <input
        autoFocus
        value={element.content.label || ''}
        onChange={e => onUpdateContent({ label: e.target.value, customLabel: e.target.value })}
        onFocus={e => e.target.select()}
        onBlur={() => setEditing(false)}
        onKeyDown={e => e.key === 'Enter' && setEditing(false)}
        className="w-full font-bold text-blue-800 bg-transparent border-none focus:outline-none px-2 pt-[2px] pb-[2px] uppercase"
        style={sectionTextStyle}
      />
    );
  }

  const baseClasses = 'w-full h-full flex items-start px-2 pt-[2px] pb-[2px] font-bold text-blue-800 uppercase';

  switch (sectionStyle) {
    case 'header-bar':
      return (
        <div className={`${baseClasses} bg-blue-50/70 border-b-2 border-blue-300`} style={sectionTextStyle}>
          {label}
        </div>
      );
    case 'underline':
      return (
        <div className={`${baseClasses} border-b-2 border-blue-400`} style={sectionTextStyle}>
          {label}
        </div>
      );
    case 'boxed':
      return (
        <div className={`${baseClasses} border-2 border-blue-300 rounded bg-blue-50/30`} style={sectionTextStyle}>
          {label}
        </div>
      );
    case 'plain':
      return (
        <div className={baseClasses} style={sectionTextStyle}>
          {label}
        </div>
      );
    default:
      return (
        <div className={`${baseClasses} bg-blue-50/50 border-b-2 border-blue-300`} style={sectionTextStyle}>
          {label}
        </div>
      );
  }
}

function renderChord(
  element: ChartElement,
  editing: boolean,
  setEditing: (v: boolean) => void,
  onUpdateContent: (c: Record<string, any>) => void,
  textareaRef?: RefObject<HTMLTextAreaElement>
) {
  const mode = element.content.mode || 'standard';
  const displayStyle = element.content.displayStyle || 'plain';
  const fontSize = element.content.fontSize ? `${element.content.fontSize}px` : '18px';

  if (editing) {
    const isFreeform = mode === 'freeform';
    return (
      <textarea
        ref={textareaRef}
        autoFocus
        value={isFreeform ? (element.content.text || element.content.chord || '') : (element.content.chord || '')}
        onChange={e => {
          const ta = e.target;
          ta.style.height = 'auto';
          const naturalH = ta.scrollHeight;
          ta.style.height = naturalH + 'px';
          if (isFreeform) {
            onUpdateContent({ text: e.target.value, _naturalHeight: naturalH });
          } else {
            onUpdateContent({ chord: e.target.value, _naturalHeight: naturalH });
          }
        }}
        onFocus={e => e.target.select()}
        onBlur={() => setEditing(false)}
        className={`w-full text-gray-900 bg-transparent border-none focus:outline-none px-2 pt-[2px] pb-[2px] resize-none overflow-hidden font-bold${isFreeform ? ' font-mono' : ''}`}
        style={{ fontSize, lineHeight: 1.25, display: 'block' }}
      />
    );
  }

  if (mode === 'freeform') {
    const text = element.content.text || element.content.chord || '';
    return (
      <div
        className="w-full h-full px-2 pt-[2px] pb-[2px] font-mono text-gray-900 whitespace-pre-wrap overflow-hidden"
        style={{ fontSize: element.content.fontSize ? `${element.content.fontSize}px` : '14px', lineHeight: 1.25 }}
      >
        {text}
      </div>
    );
  }

  const chordText = element.content.chord || '?';

  if (displayStyle === 'plain') {
    return (
      <div
        className="w-full h-full px-2 pt-[2px] pb-[2px] font-bold text-gray-900 whitespace-pre-wrap overflow-hidden"
        style={{ fontSize, lineHeight: 1.25 }}
      >
        {chordText}
      </div>
    );
  }

  return (
    <div
      className="w-full h-full px-2 pt-[2px] pb-[2px] font-bold text-gray-900 whitespace-pre-wrap overflow-hidden bg-amber-50 border border-amber-200 rounded"
      style={{ fontSize, lineHeight: 1.25 }}
    >
      {chordText}
    </div>
  );
}

function renderTextBlock(
  element: ChartElement,
  editing: boolean,
  setEditing: (v: boolean) => void,
  onUpdateContent: (c: Record<string, any>) => void,
  textareaRef: RefObject<HTMLTextAreaElement> | undefined,
  opts: { placeholder: string; defaultStyle: string; className: string }
) {
  const fontSize = element.content.fontSize ? `${element.content.fontSize}px` : '14px';
  const textAlign = (element.content.alignment || 'left') as React.CSSProperties['textAlign'];
  const fontWeight = element.content.fontWeight || 'normal';
  const fontStyleVal = element.content.fontStyle || (element.type === 'lyrics' ? 'italic' : opts.defaultStyle);

  if (editing) {
    return (
      <textarea
        ref={textareaRef}
        autoFocus
        value={element.content.text || ''}
        onChange={e => {
          const ta = e.target;
          ta.style.height = 'auto';
          const naturalH = ta.scrollHeight;
          ta.style.height = naturalH + 'px';
          onUpdateContent({ text: e.target.value, _naturalHeight: naturalH });
        }}
        onFocus={e => e.target.select()}
        onBlur={() => setEditing(false)}
        className={`w-full text-gray-800 bg-transparent border-none focus:outline-none px-2 pt-[2px] pb-[2px] resize-none overflow-hidden ${opts.className}`}
        style={{ fontSize, textAlign, fontWeight, fontStyle: fontStyleVal, lineHeight: 1.3, height: element.height, display: 'block' }}
      />
    );
  }

  return (
    <div
      className={`w-full h-full px-2 pt-[2px] pb-[2px] text-gray-800 whitespace-pre-wrap overflow-hidden ${opts.className}`}
      style={{ fontSize, textAlign, fontWeight, fontStyle: fontStyleVal, lineHeight: 1.3 }}
    >
      {element.content.text || opts.placeholder}
    </div>
  );
}

function alignToFlex(align: React.CSSProperties['textAlign']): string {
  if (align === 'center') return 'center';
  if (align === 'right') return 'flex-end';
  return 'flex-start';
}

const SONG_FORM_NAME_SUGGESTIONS = [
  'Intro', 'Verse', 'Pre-Chorus', 'Chorus', 'Bridge',
  'Solo', 'Breakdown', 'Interlude', 'Vamp', 'Tag', 'Outro',
];
const ABC_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function SongFormEditor({
  element,
  onUpdateContent,
}: {
  element: ChartElement;
  onUpdateContent: (c: Record<string, any>) => void;
}) {
  const mode: 'names' | 'abc' = element.content.mode || 'names';

  // Backward compat: fall back to legacy `entries` if new fields are absent
  const namesEntries: string[] =
    element.content.namesEntries ??
    (mode === 'names' ? element.content.entries : undefined) ??
    ['Intro'];
  const abcEntries: string[] =
    element.content.abcEntries ??
    (mode === 'abc' ? element.content.entries : undefined) ??
    ['A'];

  const activeEntries = mode === 'abc' ? abcEntries : namesEntries;

  const containerRef = useRef<HTMLDivElement>(null);
  const focusIndexRef = useRef<number | null>(null);

  // Runs after every render — focuses the queued index then clears it
  useEffect(() => {
    if (focusIndexRef.current !== null && containerRef.current) {
      const inputs = containerRef.current.querySelectorAll<HTMLInputElement>('input');
      const input = inputs[focusIndexRef.current];
      if (input) { input.focus(); input.select(); }
      focusIndexRef.current = null;
    }
  });

  const setActiveEntries = (next: string[]) => {
    if (mode === 'abc') {
      onUpdateContent({ abcEntries: next });
    } else {
      onUpdateContent({ namesEntries: next });
    }
  };

  // Mode switching preserves each set independently
  const setMode = (m: 'names' | 'abc') => {
    if (mode === m) return;
    onUpdateContent({ mode: m });
  };

  const addEntry = () => {
    let newEntry: string;
    if (mode === 'abc') {
      newEntry = abcEntries.length < ABC_LETTERS.length ? ABC_LETTERS[abcEntries.length] : '';
    } else {
      const used = new Set(namesEntries.map(e => e.toLowerCase()));
      newEntry = SONG_FORM_NAME_SUGGESTIONS.find(s => !used.has(s.toLowerCase())) ?? '';
    }
    const next = [...activeEntries, newEntry];
    setActiveEntries(next);
    focusIndexRef.current = next.length - 1;
  };

  const handleEntryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, i: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const next = [...activeEntries.slice(0, i + 1), '', ...activeEntries.slice(i + 1)];
      setActiveEntries(next);
      focusIndexRef.current = i + 1;
    }
  };

  return (
    <div
      ref={containerRef}
      className="w-full h-full px-2 pt-1 pb-1 flex flex-col gap-0.5 overflow-auto"
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="flex gap-1 mb-0.5 shrink-0">
        {(['names', 'abc'] as const).map(m => (
          <button
            key={m}
            onMouseDown={e => e.stopPropagation()}
            onClick={() => setMode(m)}
            className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
              mode === m
                ? 'bg-blue-100 border-blue-300 text-blue-700'
                : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
            }`}
          >
            {m === 'names' ? 'Names' : 'A/B/C'}
          </button>
        ))}
      </div>

      {activeEntries.map((entry, i) => (
        <div key={i} className="flex items-center gap-1 shrink-0">
          <input
            autoFocus={i === 0}
            value={entry}
            placeholder={
              mode === 'abc'
                ? (ABC_LETTERS[i] || '')
                : (SONG_FORM_NAME_SUGGESTIONS[i % SONG_FORM_NAME_SUGGESTIONS.length] || '')
            }
            onChange={e => setActiveEntries(activeEntries.map((v, idx) => idx === i ? e.target.value : v))}
            onKeyDown={e => handleEntryKeyDown(e, i)}
            className="flex-1 min-w-0 text-xs text-gray-900 bg-white border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-blue-400"
          />
          <button
            onMouseDown={e => e.stopPropagation()}
            onClick={() => setActiveEntries(activeEntries.filter((_, idx) => idx !== i))}
            className="shrink-0 text-gray-400 hover:text-red-500 text-base leading-none px-0.5"
            title="Remove"
          >×</button>
        </div>
      ))}

      <button
        onMouseDown={e => e.stopPropagation()}
        onClick={addEntry}
        className="shrink-0 text-left text-[10px] text-blue-500 hover:text-blue-700 mt-0.5"
      >
        + Add entry
      </button>
    </div>
  );
}

function renderSongForm(
  element: ChartElement,
  editing: boolean,
  onUpdateContent: (c: Record<string, any>) => void,
) {
  const mode = (element.content.mode as string) || 'names';
  // Backward compat: fall back to legacy `entries` if new fields are absent
  const namesEntries: string[] =
    element.content.namesEntries ??
    (mode === 'names' ? element.content.entries : undefined) ??
    ['Intro'];
  const abcEntries: string[] =
    element.content.abcEntries ??
    (mode === 'abc' ? element.content.entries : undefined) ??
    ['A'];
  const activeEntries = mode === 'abc' ? abcEntries : namesEntries;
  const visible = activeEntries.filter(e => e.trim());

  if (editing) {
    return <SongFormEditor element={element} onUpdateContent={onUpdateContent} />;
  }

  return (
    <div className="w-full h-full px-2 pt-[2px] pb-[2px] flex flex-col justify-start overflow-hidden">
      <div className="text-[10px] font-bold uppercase text-gray-400 tracking-wider leading-tight">
        Form
      </div>
      <div className="text-gray-900 whitespace-pre-wrap overflow-hidden" style={{ fontSize: '14px', lineHeight: 1.3 }}>
        {visible.length > 0
          ? visible.join(' → ')
          : <span className="text-gray-400 italic">Song Form...</span>
        }
      </div>
    </div>
  );
}
